import { useEffect, useState } from "react";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  Button,
  Badge,
  Input,
  Label,
  Select,
  SelectTrigger,
  SelectValue,
  SelectContent,
  SelectItem,
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  ConfirmDialog,
  ResultDialog,
} from "@/components/ui";
import { PageHeader } from "@/components/ui/page-header";
import { internalUsersApi } from "@/services/api";
import { Plus, Search, ShieldCheck, MoreHorizontal, Pencil, Power, Trash2, X, Loader2 } from "lucide-react";
import { formatDate } from "@/lib/utils";

interface InternalUser {
  id: string;
  email: string;
  name: string;
  role: "SUPER_ADMIN" | "ADMIN" | "OPERATOR";
  isActive: boolean;
  createdAt: string;
  lastLoginAt: string | null;
}

interface UserFormData {
  name: string;
  email: string;
  password: string;
  role: "SUPER_ADMIN" | "ADMIN" | "OPERATOR";
}

const roleConfig = {
  SUPER_ADMIN: { label: "Super Admin", variant: "default" as const },
  ADMIN: { label: "Admin", variant: "secondary" as const },
  OPERATOR: { label: "Operador", variant: "outline" as const },
};

const initialFormData: UserFormData = {
  name: "",
  email: "",
  password: "",
  role: "OPERATOR",
};

export function InternalUsersPage() {
  const [users, setUsers] = useState<InternalUser[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");

  // Modal states
  const [createModalOpen, setCreateModalOpen] = useState(false);
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [selectedUser, setSelectedUser] = useState<InternalUser | null>(null);
  const [formData, setFormData] = useState<UserFormData>(initialFormData);
  const [saving, setSaving] = useState(false);

  // Confirm dialog state
  const [confirmDialog, setConfirmDialog] = useState<{
    open: boolean;
    title: string;
    description: string;
    variant: "default" | "danger";
    onConfirm: () => void;
  }>({
    open: false,
    title: "",
    description: "",
    variant: "default",
    onConfirm: () => {},
  });

  // Result dialog state
  const [resultDialog, setResultDialog] = useState<{
    open: boolean;
    title: string;
    description: string;
    variant: "success" | "error";
  }>({
    open: false,
    title: "",
    description: "",
    variant: "success",
  });

  useEffect(() => {
    loadUsers();
  }, []);

  async function loadUsers() {
    try {
      const data = await internalUsersApi.list();
      setUsers(data);
    } catch (error) {
      console.error("Erro ao carregar usuários internos:", error);
    } finally {
      setLoading(false);
    }
  }

  function handleOpenCreate() {
    setFormData(initialFormData);
    setCreateModalOpen(true);
  }

  function handleOpenEdit(user: InternalUser) {
    setSelectedUser(user);
    setFormData({
      name: user.name,
      email: user.email,
      password: "",
      role: user.role,
    });
    setEditModalOpen(true);
  }

  async function handleCreate() {
    if (!formData.name || !formData.email || !formData.password) {
      setResultDialog({
        open: true,
        title: "Campos obrigatórios",
        description: "Preencha todos os campos obrigatórios.",
        variant: "error",
      });
      return;
    }

    setSaving(true);
    try {
      await internalUsersApi.create(formData);
      setCreateModalOpen(false);
      setResultDialog({
        open: true,
        title: "Usuário criado",
        description: `O usuário ${formData.name} foi criado com sucesso.`,
        variant: "success",
      });
      loadUsers();
    } catch (error) {
      console.error("Erro ao criar usuário:", error);
      setResultDialog({
        open: true,
        title: "Erro ao criar usuário",
        description: "Não foi possível criar o usuário. Verifique se o email já está em uso.",
        variant: "error",
      });
    } finally {
      setSaving(false);
    }
  }

  async function handleUpdate() {
    if (!selectedUser || !formData.name || !formData.email) {
      setResultDialog({
        open: true,
        title: "Campos obrigatórios",
        description: "Preencha todos os campos obrigatórios.",
        variant: "error",
      });
      return;
    }

    setSaving(true);
    try {
      const updateData: Partial<UserFormData> = {
        name: formData.name,
        email: formData.email,
        role: formData.role,
      };
      if (formData.password) {
        updateData.password = formData.password;
      }
      await internalUsersApi.update(selectedUser.id, updateData);
      setEditModalOpen(false);
      setResultDialog({
        open: true,
        title: "Usuário atualizado",
        description: `O usuário ${formData.name} foi atualizado com sucesso.`,
        variant: "success",
      });
      loadUsers();
    } catch (error) {
      console.error("Erro ao atualizar usuário:", error);
      setResultDialog({
        open: true,
        title: "Erro ao atualizar usuário",
        description: "Não foi possível atualizar o usuário.",
        variant: "error",
      });
    } finally {
      setSaving(false);
    }
  }

  function handleToggleStatus(user: InternalUser) {
    const action = user.isActive ? "desativar" : "ativar";
    setConfirmDialog({
      open: true,
      title: `${user.isActive ? "Desativar" : "Ativar"} usuário`,
      description: `Tem certeza que deseja ${action} o usuário "${user.name}"?`,
      variant: "default",
      onConfirm: async () => {
        try {
          await internalUsersApi.update(user.id, { isActive: !user.isActive });
          setConfirmDialog((prev) => ({ ...prev, open: false }));
          setResultDialog({
            open: true,
            title: `Usuário ${user.isActive ? "desativado" : "ativado"}`,
            description: `O usuário ${user.name} foi ${user.isActive ? "desativado" : "ativado"} com sucesso.`,
            variant: "success",
          });
          loadUsers();
        } catch (error) {
          console.error("Erro ao alterar status:", error);
          setConfirmDialog((prev) => ({ ...prev, open: false }));
          setResultDialog({
            open: true,
            title: "Erro",
            description: `Não foi possível ${action} o usuário.`,
            variant: "error",
          });
        }
      },
    });
  }

  function handleDelete(user: InternalUser) {
    setConfirmDialog({
      open: true,
      title: "Excluir usuário",
      description: `Tem certeza que deseja excluir o usuário "${user.name}"? Esta ação não pode ser desfeita.`,
      variant: "danger",
      onConfirm: async () => {
        try {
          await internalUsersApi.delete(user.id);
          setConfirmDialog((prev) => ({ ...prev, open: false }));
          setResultDialog({
            open: true,
            title: "Usuário excluído",
            description: `O usuário ${user.name} foi excluído com sucesso.`,
            variant: "success",
          });
          loadUsers();
        } catch (error) {
          console.error("Erro ao excluir usuário:", error);
          setConfirmDialog((prev) => ({ ...prev, open: false }));
          setResultDialog({
            open: true,
            title: "Erro ao excluir",
            description: "Não foi possível excluir o usuário.",
            variant: "error",
          });
        }
      },
    });
  }

  const filteredUsers = users.filter(
    (u) =>
      u.name.toLowerCase().includes(search.toLowerCase()) ||
      u.email.toLowerCase().includes(search.toLowerCase())
  );

  const activeUsers = users.filter((u) => u.isActive).length;
  const inactiveUsers = users.filter((u) => !u.isActive).length;
  const superAdmins = users.filter((u) => u.role === "SUPER_ADMIN").length;

  return (
    <div className="p-4 sm:p-6 space-y-4 sm:space-y-6">
      <PageHeader
        icon={<ShieldCheck className="w-5 h-5 sm:w-6 sm:h-6" />}
        title="Usuários Internos"
        description="Gerenciamento de administradores do sistema"
        stats={[
          { value: users.length, label: "Total" },
          { value: activeUsers, label: "Ativos" },
          { value: inactiveUsers, label: "Inativos" },
          { value: superAdmins, label: "Super Admin" },
        ]}
        actions={
          <Button
            className="bg-white/20 hover:bg-white/30 text-white border-0"
            onClick={handleOpenCreate}
          >
            <Plus className="w-4 h-4 mr-2" />
            Novo Admin
          </Button>
        }
      />
      {/* Actions Bar */}
      <div className="flex items-center justify-between gap-4">
        <div className="relative flex-1 max-w-md">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
          <Input
            placeholder="Buscar por nome, email..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="pl-9"
          />
        </div>
      </div>

      {/* Users Table */}
      <Card>
        <CardContent className="p-0">
            {loading ? (
              <div className="p-8 text-center text-muted-foreground">
                Carregando...
              </div>
            ) : filteredUsers.length === 0 ? (
              <div className="p-8 text-center text-muted-foreground">
                <ShieldCheck className="w-12 h-12 mx-auto mb-4 opacity-50" />
                <p>Nenhum usuário interno encontrado</p>
              </div>
            ) : (
              <table className="w-full">
                <thead className="border-b border-border">
                  <tr className="text-left text-sm text-muted-foreground">
                    <th className="p-4 font-medium">Usuário</th>
                    <th className="p-4 font-medium">Role</th>
                    <th className="p-4 font-medium">Status</th>
                    <th className="p-4 font-medium">Criado em</th>
                    <th className="p-4 font-medium">Último acesso</th>
                    <th className="p-4 font-medium">Ações</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredUsers.map((user) => (
                    <tr
                      key={user.id}
                      className="border-b border-border last:border-0 hover:bg-muted/50"
                    >
                      <td className="p-4">
                        <div>
                          <p className="font-medium">{user.name}</p>
                          <p className="text-sm text-muted-foreground">
                            {user.email}
                          </p>
                        </div>
                      </td>
                      <td className="p-4">
                        <Badge variant={roleConfig[user.role].variant}>
                          {roleConfig[user.role].label}
                        </Badge>
                      </td>
                      <td className="p-4">
                        <Badge variant={user.isActive ? "success" : "secondary"}>
                          {user.isActive ? "Ativo" : "Inativo"}
                        </Badge>
                      </td>
                      <td className="p-4 text-sm text-muted-foreground">
                        {formatDate(user.createdAt)}
                      </td>
                      <td className="p-4 text-sm text-muted-foreground">
                        {user.lastLoginAt ? formatDate(user.lastLoginAt) : "Nunca"}
                      </td>
                      <td className="p-4">
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <Button size="sm" variant="ghost">
                              <MoreHorizontal className="w-4 h-4" />
                            </Button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="end">
                            <DropdownMenuItem onClick={() => handleOpenEdit(user)}>
                              <Pencil className="w-4 h-4 mr-2" />
                              Editar
                            </DropdownMenuItem>
                            <DropdownMenuItem onClick={() => handleToggleStatus(user)}>
                              <Power className="w-4 h-4 mr-2" />
                              {user.isActive ? "Desativar" : "Ativar"}
                            </DropdownMenuItem>
                            <DropdownMenuSeparator />
                            <DropdownMenuItem
                              onClick={() => handleDelete(user)}
                              className="text-destructive focus:text-destructive"
                            >
                              <Trash2 className="w-4 h-4 mr-2" />
                              Excluir
                            </DropdownMenuItem>
                          </DropdownMenuContent>
                        </DropdownMenu>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
          )}
        </CardContent>
      </Card>

      {/* Create Modal */}
      {createModalOpen && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <Card className="w-full max-w-lg mx-4 max-h-[90vh] overflow-y-auto">
            <CardHeader className="flex flex-row items-center justify-between">
              <CardTitle>Novo Administrador</CardTitle>
              <Button variant="ghost" size="sm" onClick={() => setCreateModalOpen(false)}>
                <X className="w-4 h-4" />
              </Button>
            </CardHeader>
            <CardContent className="space-y-4">
              <p className="text-sm text-muted-foreground -mt-2">
                Preencha os dados para criar um novo usuário interno.
              </p>

              <div className="space-y-2">
                <Label htmlFor="name">Nome *</Label>
                <Input
                  id="name"
                  placeholder="Nome completo"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="email">Email *</Label>
                <Input
                  id="email"
                  type="email"
                  placeholder="email@exemplo.com"
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="password">Senha *</Label>
                <Input
                  id="password"
                  type="password"
                  placeholder="Mínimo 8 caracteres"
                  value={formData.password}
                  onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="role">Perfil *</Label>
                <Select
                  value={formData.role}
                  onValueChange={(value: "SUPER_ADMIN" | "ADMIN" | "OPERATOR") =>
                    setFormData({ ...formData, role: value })
                  }
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Selecione o perfil" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="OPERATOR">Operador</SelectItem>
                    <SelectItem value="ADMIN">Admin</SelectItem>
                    <SelectItem value="SUPER_ADMIN">Super Admin</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div className="flex justify-end gap-2 pt-4">
                <Button variant="outline" onClick={() => setCreateModalOpen(false)}>
                  Cancelar
                </Button>
                <Button onClick={handleCreate} disabled={saving}>
                  {saving && <Loader2 className="w-4 h-4 animate-spin mr-2" />}
                  Criar usuário
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Edit Modal */}
      {editModalOpen && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <Card className="w-full max-w-lg mx-4 max-h-[90vh] overflow-y-auto">
            <CardHeader className="flex flex-row items-center justify-between">
              <CardTitle>Editar Administrador</CardTitle>
              <Button variant="ghost" size="sm" onClick={() => setEditModalOpen(false)}>
                <X className="w-4 h-4" />
              </Button>
            </CardHeader>
            <CardContent className="space-y-4">
              <p className="text-sm text-muted-foreground -mt-2">
                Atualize os dados do usuário. Deixe a senha em branco para manter a atual.
              </p>

              <div className="space-y-2">
                <Label htmlFor="edit-name">Nome *</Label>
                <Input
                  id="edit-name"
                  placeholder="Nome completo"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="edit-email">Email *</Label>
                <Input
                  id="edit-email"
                  type="email"
                  placeholder="email@exemplo.com"
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="edit-password">Nova Senha (opcional)</Label>
                <Input
                  id="edit-password"
                  type="password"
                  placeholder="Deixe em branco para manter"
                  value={formData.password}
                  onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="edit-role">Perfil *</Label>
                <Select
                  value={formData.role}
                  onValueChange={(value: "SUPER_ADMIN" | "ADMIN" | "OPERATOR") =>
                    setFormData({ ...formData, role: value })
                  }
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Selecione o perfil" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="OPERATOR">Operador</SelectItem>
                    <SelectItem value="ADMIN">Admin</SelectItem>
                    <SelectItem value="SUPER_ADMIN">Super Admin</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div className="flex justify-end gap-2 pt-4">
                <Button variant="outline" onClick={() => setEditModalOpen(false)}>
                  Cancelar
                </Button>
                <Button onClick={handleUpdate} disabled={saving}>
                  {saving && <Loader2 className="w-4 h-4 animate-spin mr-2" />}
                  Salvar alterações
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Confirm Dialog */}
      <ConfirmDialog
        open={confirmDialog.open}
        onClose={() => setConfirmDialog((prev) => ({ ...prev, open: false }))}
        title={confirmDialog.title}
        description={confirmDialog.description}
        variant={confirmDialog.variant}
        onConfirm={confirmDialog.onConfirm}
      />

      {/* Result Dialog */}
      <ResultDialog
        open={resultDialog.open}
        onClose={() => setResultDialog((prev) => ({ ...prev, open: false }))}
        title={resultDialog.title}
        description={resultDialog.description}
        variant={resultDialog.variant}
      />
    </div>
  );
}
